import {
    ArrowUturnLeftIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import { ColumnPinningState } from '@tanstack/react-table';
import { useEffect } from 'react';

import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { Button } from '../Button';
import { Icon } from '../Icon';
import { preferenceKeys } from './GridPreferencesConfig';

interface BurgerMenuProps {
    columns: any[];
    columnVisibility: { [key: string]: boolean };
    isOpen: boolean;
    storedStateKey?: any;
    setWrapContent?: any;
    wrapContent?: boolean;
    onClose: () => void;
    toggleColumnVisibility: (accessorKey: string) => void;
    resetColumnsWidth: (e: any) => void;
    resetColumnsSort: (e: any) => void;
    // LAX-10039: full reset mirroring the legacy `Reset` grid-menu entry.
    // Clears the persisted state for the current key/sub-key on both the
    // local blob and the server preferences and reverts every column state
    // (size, visibility, order, sort, pinning) to the column defs' defaults.
    resetGridState?: (e: any) => void;

    currentColumnWidths: any;
    defaultColumnWidths: any;
    currentColumnOrder: any[];
    defaultColumnOrder: any[];

    // LAX-10037: user-controlled column pinning. `columnPinning` is the
    // effective TanStack state (defaults + user pins merged) so the burger
    // menu correctly highlights the active side; `setColumnPin` is the
    // toggle callback wired to Grid.tsx.
    columnPinning?: ColumnPinningState;
    setColumnPin?: (columnId: string, position: 'left' | 'right' | null) => void;
}

const GridBurgerMenu: React.FC<BurgerMenuProps> = ({
    columns,
    columnVisibility,
    isOpen,
    toggleColumnVisibility,
    storedStateKey,
    setWrapContent,
    wrapContent,
    onClose,
    resetColumnsWidth,
    resetColumnsSort,
    resetGridState,
    currentColumnWidths,
    defaultColumnWidths,
    currentColumnOrder,
    defaultColumnOrder,
    columnPinning,
    setColumnPin,
}) => {
    const accessKey = preferenceKeys[storedStateKey] || '';
    const { t } = useTranslation();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const menu = document.getElementById('burger-menu');
            const button = document.getElementById('burger-button');
            if (menu && !menu.contains(event.target as Node) && button && !button.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    const wrapVisible = () => {
        const validAccessKeys = [
            'editor-history-grid-states-timeline',
            'editor-history-grid-states-attributes',
            'editor-history-grid-states-events'
        ];

        return validAccessKeys.includes(accessKey);
    };

    const handleWrapChange = () => {
        setWrapContent((prev: any) => !prev);
    };

    const visibleColumns = columns.filter(
        c => c.accessorKey !== 'actions' && c.accessorKey !== 'select'
    );

    const allColumnsSelected = visibleColumns.every(
        c => columnVisibility[c.id || c.accessorKey]
    );

    const toggleAllColumns = (e: any) => {
        e.preventDefault();
        visibleColumns.forEach(column => {
            const key = column.id || column.accessorKey;
            const isVisible = columnVisibility[key];
            if (allColumnsSelected && isVisible) {
                toggleColumnVisibility(key);
            } else if (!allColumnsSelected && !isVisible) {
                toggleColumnVisibility(key);
            }
        });
    };

    const hasWidthChanged = JSON.stringify(currentColumnWidths) !== JSON.stringify(defaultColumnWidths);
    const hasOrderChanged = JSON.stringify(currentColumnOrder) !== JSON.stringify(defaultColumnOrder);

    if (!isOpen) return null;

    return (
        <div
            id="burger-menu"
            className="tw-absolute tw-top-full
            tw-right-0 tw-mt-2
            tw-bg-white tw-shadow-lg
            tw-p-4
            tw-rounded-lg
            tw-w-max
            tw-min-w-[200px] tw-max-h-[300px] tw-overflow-y-auto tw-z-50 tw-text-t-sm tw-space-y-2"
        >
            <div>
                <Button
                    variant="outline"
                    onClick={(e) => resetColumnsWidth(e)}
                    disabled={!hasWidthChanged}
                    className="tw-flex tw-items-center tw-space-x-2 tw-w-fit tw-py-1"
                >
                    <Icon disabled={!hasWidthChanged} variant="secondary" icon={ArrowUturnLeftIcon} size="xs" className="tw-align-middle tw-mb-1"/>
                    <span className="tw-pl-1.5 tw-align-middle">{t('RESET_COL_WIDTH')}</span>
                </Button>
            </div>
            <div>
                <Button
                    variant="outline"
                    onClick={(e) => resetColumnsSort(e)}
                    disabled={!hasOrderChanged}
                    className="tw-flex tw-items-center tw-space-x-2 tw-w-fit tw-py-1"
                >
                    <Icon disabled={!hasOrderChanged} variant="secondary" icon={ArrowUturnLeftIcon} size="xs" className="tw-align-middle tw-mb-1"/>
                    <span className="tw-pl-1.5 tw-align-middle">{t('RESET_COL_ORDER')}</span>
                </Button>
            </div>
            {resetGridState && (
                <div>
                    <Button
                        variant="outline"
                        onClick={(e) => resetGridState(e)}
                        className="tw-flex tw-items-center tw-space-x-2 tw-w-fit tw-py-1"
                    >
                        <Icon variant="secondary" icon={ArrowUturnLeftIcon} size="xs" className="tw-align-middle tw-mb-1"/>
                        <span className="tw-pl-1.5 tw-align-middle">{t('RESET_GRID_COLUMNS')}</span>
                    </Button>
                </div>
            )}

            <hr className="!tw-my-4 tw-border-gray-300" />

            <ul className="tw-list-none tw-p-0 tw-m-0">
                {wrapVisible() && (
                    <div>
                        <li className="tw-flex tw-items-center tw-space-x-2">
                            <input
                                type="checkbox"
                                onChange={handleWrapChange}
                                checked={wrapContent}
                                className="tw-cursor-pointer"
                            />
                            <span>Wrap text</span>
                        </li>
                        <hr className="!tw-my-4 tw-border-gray-300" />
                    </div>
                )}

                <li className="tw-flex tw-items-center tw-space-x-2">
                    <input
                        type="checkbox"
                        checked={allColumnsSelected}
                        onChange={(e:any) => toggleAllColumns(e)}
                        className="tw-cursor-pointer"
                    />
                    <span>Show all columns</span>
                </li>

                <ul className="tw-ml-2 tw-pl-[15px] tw-pt-[5px]">
                    {visibleColumns.map((column) => {
                        const colId = column.id || column.accessorKey;
                        const isPinnedLeft = (columnPinning?.left ?? []).includes(colId);
                        const isPinnedRight = (columnPinning?.right ?? []).includes(colId);
                        const canPin = !!setColumnPin;
                        return (
                            <li
                                key={colId}
                                className="tw-flex tw-items-center tw-space-x-2 tw-py-1"
                            >
                                <label
                                    className="tw-flex tw-items-center tw-space-x-2 tw-cursor-pointer tw-flex-1"
                                    onClick={() => toggleColumnVisibility(colId)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={columnVisibility[colId]}
                                        readOnly
                                        className="tw-cursor-pointer"
                                    />
                                    <span>{column.header}</span>
                                </label>
                                {canPin && (
                                    <span className="tw-flex tw-items-center tw-space-x-1 tw-ml-2">
                                        <Button
                                            variant="outline"
                                            title={isPinnedLeft ? t('UNPIN_COLUMN') : t('PIN_COLUMN_LEFT')}
                                            aria-label={isPinnedLeft ? t('UNPIN_COLUMN') : t('PIN_COLUMN_LEFT')}
                                            aria-pressed={isPinnedLeft}
                                            className={cn(
                                                '!tw-px-1 !tw-py-0.5',
                                                isPinnedLeft && '!tw-bg-fill-secondary',
                                            )}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setColumnPin!(colId, isPinnedLeft ? null : 'left');
                                            }}
                                        >
                                            <Icon
                                                icon={ChevronDoubleLeftIcon}
                                                size="2xs"
                                                variant={isPinnedLeft ? 'default' : 'secondary'}
                                            />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            title={isPinnedRight ? t('UNPIN_COLUMN') : t('PIN_COLUMN_RIGHT')}
                                            aria-label={isPinnedRight ? t('UNPIN_COLUMN') : t('PIN_COLUMN_RIGHT')}
                                            aria-pressed={isPinnedRight}
                                            className={cn(
                                                '!tw-px-1 !tw-py-0.5',
                                                isPinnedRight && '!tw-bg-fill-secondary',
                                            )}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setColumnPin!(colId, isPinnedRight ? null : 'right');
                                            }}
                                        >
                                            <Icon
                                                icon={ChevronDoubleRightIcon}
                                                size="2xs"
                                                variant={isPinnedRight ? 'default' : 'secondary'}
                                            />
                                        </Button>
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </ul>
        </div>
    );
};

export default GridBurgerMenu;
